import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';

// Import components
import { LoginPage, AuthCallback } from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { translations } from './translations';
import { api } from './components/shared/api';
import { Icons } from './components/shared/Icons';
import { formatDateTime, formatDateShort, setUserTimezone } from './components/shared/timezone';
import { AppDialogProvider, useToast, useConfirm } from './components/shared/AppDialogs';
// Import AgentDetailPage from refactored agent-editor module
import { AgentDetailPage as LegacyAgentDetailPage } from './components/agent-editor/AgentDetailPage';
// Import Admin Page
import { AdminPage } from './components/admin/AdminPage';
// Import Billing Page
import { BillingPage } from './components/billing/BillingPage';
// Import Landing Page
import { LandingPage } from './components/landing/LandingPage';
// Import Call History Page
import { CallHistoryPage } from './components/history/CallHistoryPage';
// Import Settings Page
import { SettingsPage } from './components/settings/SettingsPage';
// Import Campaigns Page
import { CampaignsPage } from './components/campaigns/CampaignsPage';
// Import Call Queue Page
import { CallQueuePage } from './components/queue/CallQueuePage';
// Import Legal Pages
import { PrivacyPage } from './components/legal/PrivacyPage';
import { TermsPage } from './components/legal/TermsPage';
// Import Usage Analytics Page
import { UsageAnalyticsPage } from './components/usage/UsageAnalyticsPage';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Main App Router - handles auth callback before protected routes
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Legal Pages - Public */}
      <Route path="/datenschutz" element={<PrivacyPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/nutzungsbedingungen" element={<TermsPage />} />
      <Route path="/terms" element={<TermsPage />} />
      {/* Protected Dashboard Routes */}
      <Route 
        path="/dashboard/*" 
        element={
          <ProtectedRoute>
            <MainApp />
          </ProtectedRoute>
        } 
      />
      {/* Redirect old paths to dashboard */}
      <Route 
        path="/agents" 
        element={
          <ProtectedRoute>
            <MainApp initialPage="agents" />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

// Main authenticated app
function MainApp({ user: initialUser, initialPage }) {
  const [currentPage, setCurrentPage] = useState(initialPage || 'dashboard');
  const [lang, setLang] = useState('de');
  const [agents, setAgents] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({});
  const [toolTemplates, setToolTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [highlightSessionId, setHighlightSessionId] = useState(null);
  const [user, setUser] = useState(initialUser);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const t = translations[lang];

  // Check admin status and load timezone
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await api.get('/admin/check');
        setIsAdmin(res.is_admin || false);
      } catch (e) {
        setIsAdmin(false);
      }
    };
    const loadTimezone = async () => {
      try {
        const res = await api.get('/settings/company-profile');
        if (res.success && res.data?.timezone) setUserTimezone(res.data.timezone);
      } catch (e) { /* ignore */ }
    };
    checkAdmin();
    loadTimezone();
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsRes, sessionsRes, statsRes, templatesRes] = await Promise.all([
        api.get('/agents'),
        api.get('/sessions'),
        api.get('/stats'),
        api.get('/tool-templates'),
      ]);
      
      if (agentsRes.success) setAgents(agentsRes.data || []);
      if (sessionsRes.success) setSessions(sessionsRes.data || []);
      if (statsRes.success) setStats(statsRes.data || {});
      if (templatesRes.success) setToolTemplates(templatesRes.data || []);
    } catch (error) {

    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Logout handler
  const handleLogout = async () => {
    await api.post('/auth/logout', {});
    navigate('/login');
  };

  // Navigation items
  const navItems = [
    { id: 'dashboard', label: t.dashboard, icon: Icons.Dashboard },
    { id: 'agents', label: 'Agenten', icon: Icons.Agents },
    { id: 'campaigns', label: 'Kampagnen', icon: Icons.Campaigns },
    { id: 'queue', label: 'Live-Queue', icon: Icons.Queue },
    { id: 'transcripts', label: 'Historie', icon: Icons.History },
    { id: 'usage', label: 'Nutzung', icon: Icons.Usage },
    { id: 'settings', label: 'Einstellungen', icon: Icons.Settings },
    { id: 'billing', label: 'Abrechnung', icon: Icons.Billing },
  ];

  // Reload user data (credits, etc.)
  const reloadUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      if (res.success && res.user) {
        setUser(res.user);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Refresh credits when switching pages
  useEffect(() => {
    reloadUser();
  }, [currentPage, reloadUser]);

  // Open/close agent detail
  const openAgentDetail = (agentId) => {
    setSelectedAgentId(agentId);
    setCurrentPage('agent-detail');
  };

  const [agentEditorDirty, setAgentEditorDirty] = useState(false);

  const closeAgentDetail = () => {
    setSelectedAgentId(null);
    setCurrentPage('agents');
    setAgentEditorDirty(false);
    loadData();
  };

  const safeCloseAgentDetail = async () => {
    if (agentEditorDirty) {
      const ok = await confirm({
        title: 'Ungespeicherte Änderungen',
        message: 'Sie haben Änderungen am Agenten vorgenommen, die noch nicht gespeichert wurden. Möchten Sie die Seite wirklich verlassen?',
        confirmText: 'Verlassen',
        cancelText: 'Zurück zum Editor',
        type: 'warning',
      });
      if (ok) closeAgentDetail();
    } else {
      closeAgentDetail();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img src="/wisecall-logo.png" alt="Wisecall" className="h-8 w-auto" />
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
          aria-label="Menu"
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile, shown on lg+ */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo - Hidden on mobile (shown in mobile header) */}
        <div className="hidden lg:flex h-16 items-center px-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img src="/wisecall-logo.png" alt="Wisecall" className="h-12 w-auto" />
          </div>
        </div>

        {/* Mobile close button */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="font-semibold text-gray-800">Menü</span>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* User info */}
        {user && (
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm">{user.name?.charAt(0)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
            {/* Credits info */}
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-gray-500">{t.credits}:</span>
              <span className="font-medium text-blue-600">
                {user.credits - (user.credits_used || 0)} Min
              </span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setCurrentPage(item.id); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                currentPage === item.id
                  ? 'bg-blue-50 text-blue-600 font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
          
          {/* Admin Link - only visible for admins */}
          {isAdmin && (
            <button
              onClick={() => { setCurrentPage('admin'); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                currentPage === 'admin'
                  ? 'bg-red-50 text-red-600 font-medium'
                  : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <Icons.Settings />
              <span>Admin Panel</span>
            </button>
          )}
        </nav>

        {/* Language Toggle & Logout */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          <div className="flex items-center justify-between bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setLang('de')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                lang === 'de' ? 'bg-white shadow text-gray-800' : 'text-gray-500'
              }`}
            >
              🇩🇪 DE
            </button>
            <button
              onClick={() => setLang('en')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                lang === 'en' ? 'bg-white shadow text-gray-800' : 'text-gray-500'
              }`}
            >
              🇬🇧 EN
            </button>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
          >
            <Icons.Logout />
            <span>{t.logout}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto min-h-screen lg:min-h-0">
        {/* Header - Hidden on mobile (has own header) */}
        <header className="hidden lg:flex h-16 bg-white border-b border-gray-200 items-center justify-between px-8">
          <h1 className="text-xl font-semibold text-gray-800">
            {currentPage === 'agent-detail' ? (
              <button onClick={safeCloseAgentDetail} className="flex items-center gap-2 hover:text-blue-600">
                <span>←</span> Zurück zu Agenten
              </button>
            ) : (
              navItems.find(i => i.id === currentPage)?.label || t.dashboard
            )}
          </h1>
          {currentPage === 'agents' && (
            <button
              onClick={() => { setEditingAgent(null); setShowAgentModal(true); }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Icons.Plus />
              {t.createAgent}
            </button>
          )}
        </header>

        {/* Mobile Page Title & Actions */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-800">
              {currentPage === 'agent-detail' ? (
                <button onClick={safeCloseAgentDetail} className="flex items-center gap-2 hover:text-blue-600">
                  <span>←</span> Zurück
                </button>
              ) : (
                navItems.find(i => i.id === currentPage)?.label || t.dashboard
              )}
            </h1>
            {currentPage === 'agents' && (
              <button
                onClick={() => { setEditingAgent(null); setShowAgentModal(true); }}
                className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Icons.Plus />
                <span className="hidden sm:inline">{t.createAgent}</span>
              </button>
            )}
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Credit Warning Banner */}
          {user && (user.credits - (user.credits_used || 0)) <= 10 && (user.credits - (user.credits_used || 0)) > 0 && (
            <div className="mb-4 sm:mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl sm:text-2xl">⚠️</span>
                <div>
                  <p className="font-medium text-yellow-800 text-sm sm:text-base">Nur noch {user.credits - (user.credits_used || 0)} Minuten übrig!</p>
                  <p className="text-xs sm:text-sm text-yellow-600">Upgraden Sie Ihren Plan, um weitere Credits zu erhalten.</p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('billing')}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium text-sm sm:text-base w-full sm:w-auto"
              >
                Jetzt upgraden
              </button>
            </div>
          )}
          
          {/* No Credits Banner */}
          {user && (user.credits - (user.credits_used || 0)) <= 0 && (
            <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl sm:text-2xl">🚫</span>
                <div>
                  <p className="font-medium text-red-800 text-sm sm:text-base">Keine Credits mehr!</p>
                  <p className="text-xs sm:text-sm text-red-600">Ihre Agenten können keine Anrufe mehr annehmen.</p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('billing')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm sm:text-base w-full sm:w-auto"
              >
                Plan upgraden
              </button>
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            </div>
          ) : (
            <>
              {currentPage === 'dashboard' && (
                <DashboardPage stats={stats} agents={agents} sessions={sessions} t={t} user={user} onNavigate={(page, opts) => {
                  setCurrentPage(page);
                  if (opts?.sessionId) setHighlightSessionId(opts.sessionId);
                  else setHighlightSessionId(null);
                }} />
              )}
              {currentPage === 'agents' && (
                <AgentsPage
                  agents={agents}
                  t={t}
                  onEdit={(agent) => openAgentDetail(agent.id)}
                  onDelete={async (id) => {
                    const ok = await confirm({
                      title: 'Agent löschen',
                      message: 'Dieser Agent und alle zugehörigen Daten werden unwiderruflich gelöscht. Fortfahren?',
                      confirmText: 'Löschen',
                      type: 'danger',
                    });
                    if (ok) {
                      await api.delete(`/agents/${id}`);
                      toast.success('Agent wurde gelöscht');
                      loadData();
                    }
                  }}
                />
              )}
              {currentPage === 'agent-detail' && selectedAgentId && (
                <AgentDetailPage 
                  agentId={selectedAgentId}
                  onBack={safeCloseAgentDetail}
                  onDirtyChange={setAgentEditorDirty}
                  onSave={async (agentData) => {
                    const result = await api.put(`/agents/${selectedAgentId}`, agentData);
                    if (result.success) {
                      // Refresh data in background without showing loading spinner
                      const [agentsRes, sessionsRes, statsRes] = await Promise.all([
                        api.get('/agents'),
                        api.get('/sessions'),
                        api.get('/stats'),
                      ]);
                      if (agentsRes.success) setAgents(agentsRes.data || []);
                      if (sessionsRes.success) setSessions(sessionsRes.data || []);
                      if (statsRes.success) setStats(statsRes.data || {});
                    }
                    return result;
                  }}
                />
              )}
              {currentPage === 'sessions' && (
                <SessionsPage sessions={sessions} agents={agents} t={t} />
              )}
              {currentPage === 'transcripts' && (
                <CallHistoryPage highlightSessionId={highlightSessionId} onHighlightHandled={() => setHighlightSessionId(null)} />
              )}
              {currentPage === 'campaigns' && (
                <CampaignsPage agents={agents} />
              )}
              {currentPage === 'queue' && (
                <CallQueuePage agents={agents} />
              )}
              {currentPage === 'settings' && (
                <SettingsPage user={user} onUserUpdate={reloadUser} />
              )}
              {currentPage === 'billing' && (
                <BillingPage user={user} onUserUpdate={reloadUser} />
              )}
              {currentPage === 'usage' && (
                <UsageAnalyticsPage user={user} onNavigate={setCurrentPage} />
              )}
              {currentPage === 'admin' && isAdmin && (
                <AdminPage onBack={() => setCurrentPage('dashboard')} />
              )}
            </>
          )}
        </div>
      </main>

      {/* Agent Modal - only for creating new agents */}
      {showAgentModal && (
        <AgentModal
          agent={editingAgent}
          t={t}
          toolTemplates={toolTemplates}
          onClose={() => setShowAgentModal(false)}
          onSave={async (agentData) => {
            try {
              const result = await api.post('/agents', agentData);
              if (result && result.success) {
                setShowAgentModal(false);
                await loadData();
                return true;
              }
              return false;
            } catch (error) {

              return false;
            }
          }}
        />
      )}
    </div>
  );
}

// ================== PAGE COMPONENTS ==================
// (These will be moved to separate files in future refactoring)

// Dashboard Page
function DashboardPage({ stats, agents, sessions, t, user, onNavigate }) {
  const [recentCalls, setRecentCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(true);

  // Fetch recent calls from transcripts endpoint
  useEffect(() => {
    const fetchCalls = async () => {
      try {
        const res = await api.get('/transcripts?limit=10');
        if (res.success) {
          setRecentCalls(res.data || []);
        }
      } catch (e) {

      } finally {
        setCallsLoading(false);
      }
    };
    fetchCalls();
  }, []);

  const statCards = [
    { label: t.totalAgents, value: stats.total_agents || agents.length || 0, icon: '🤖', color: 'blue' },
    { label: t.activeSessions, value: stats.active_sessions || 0, icon: '📞', color: 'green' },
    { label: t.totalCalls, value: stats.total_sessions || recentCalls.length || 0, icon: '📊', color: 'purple' },
    { label: t.callDuration, value: `${Math.round((stats.total_duration || 0) / 60)} min`, icon: '⏱️', color: 'orange' },
  ];

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl lg:rounded-2xl p-4 sm:p-6 lg:p-8 text-white">
        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2">
          {t.welcomeBack}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋
        </h2>
        <p className="text-blue-100 text-sm sm:text-base">{t.welcomeDesc}</p>
        
        {/* Plan & Credits Info */}
        {user && (
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-4">
            <span className="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm">
              {t.plan}: {user.plan?.toUpperCase() || 'FREE'}
            </span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm">
              {t.credits}: {user.credits - (user.credits_used || 0)} Min
            </span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <span className="text-2xl sm:text-3xl">{stat.icon}</span>
              <span className={`text-xl sm:text-2xl lg:text-3xl font-bold text-${stat.color}-600`}>{stat.value}</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Recent Agents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">{t.agents}</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {agents.slice(0, 5).map((agent) => (
              <div key={agent.id} className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-base sm:text-lg">🤖</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 text-sm sm:text-base truncate">{agent.name}</p>
                    <p className="text-xs text-gray-500 truncate">{agent.description || (agent.language === 'de' ? '🇩🇪 Deutsch' : '🇬🇧 Englisch')}</p>
                  </div>
                </div>
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                  agent.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {agent.is_active ? t.active : t.inactive}
                </span>
              </div>
            ))}
            {agents.length === 0 && (
              <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">{t.noAgents}</div>
            )}
          </div>
        </div>

        {/* Recent Calls from ElevenLabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Letzte Anrufe</h3>
            {recentCalls.length > 0 && (
              <button
                onClick={() => onNavigate && onNavigate('transcripts')}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid="dashboard-view-all-calls"
              >
                Alle anzeigen →
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {callsLoading ? (
              <div className="px-6 py-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent mx-auto"></div>
              </div>
            ) : recentCalls.length > 0 ? (
              recentCalls.slice(0, 5).map((call) => (
                <div
                  key={call.session_id}
                  className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => onNavigate && onNavigate('transcripts', { sessionId: call.session_id })}
                  data-testid={`dashboard-call-${call.session_id}`}
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      call.call_successful === 'true' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <svg className={`w-4 h-4 ${call.call_successful === 'true' ? 'text-green-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 text-sm sm:text-base truncate">
                        {call.caller_number && call.caller_number !== 'Unbekannt' ? call.caller_number : call.agent_name}
                      </p>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                        <span className="truncate">{call.agent_name}</span>
                        <span>·</span>
                        <span>{formatDuration(call.duration_seconds)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-xs text-gray-500">
                      {call.created_at ? formatDateShort(call.created_at) : ''}
                    </p>
                    {call.title && call.title !== 'Gespräch' && (
                      <p className="text-xs text-gray-400 truncate max-w-[120px]">{call.title}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
                Noch keine Anrufe. Sobald Anrufe stattfinden, erscheinen sie hier.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Agents Page
function AgentsPage({ agents, t, onEdit, onDelete }) {
  return (
    <div className="space-y-6">
      {agents.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
          <div className="text-6xl mb-4">🤖</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.noAgents}</h3>
          <p className="text-gray-500 mb-6">{t.welcomeDesc}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {agents.map((agent) => (
            <div key={agent.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Agent Header */}
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl flex-shrink-0">
                      🤖
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-base sm:text-lg text-gray-800 truncate">{agent.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{agent.description || '-'}</p>
                    </div>
                  </div>
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                    agent.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {agent.is_active ? t.active : t.inactive}
                  </span>
                </div>
              </div>

              {/* Agent Details */}
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-gray-400">{agent.language === 'de' ? '🇩🇪' : '🇬🇧'}</span>
                    <span className="text-gray-600">{agent.language === 'de' ? 'Deutsch' : 'Englisch'}</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-gray-400">📞</span>
                    <span className="text-gray-600">{agent.total_sessions || 0} {t.sessions.toLowerCase()}</span>
                  </div>
                  {agent.phone_number && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-gray-400">🔌</span>
                      <span className="text-gray-600">{agent.phone_number}</span>
                    </div>
                  )}
                </div>

                {/* Tools */}
                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 mb-2 block">Tools</span>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {(agent.tools || []).map((tool, i) => (
                      <span key={i} className="px-2 sm:px-3 py-1 bg-gray-100 rounded-full text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                        {tool.tool_type === 'email' && '📧'}
                        {tool.tool_type === 'sms' && '💬'}
                        {tool.tool_type === 'calendar' && '📅'}
                        {tool.tool_type === 'webhook' && '🔗'}
                        <span className="hidden sm:inline">{tool.name || tool.tool_type}</span>
                      </span>
                    ))}
                    {(!agent.tools || agent.tools.length === 0) && (
                      <span className="text-xs sm:text-sm text-gray-400">Keine Tools</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 flex flex-col sm:flex-row justify-end gap-2">
                <button
                  onClick={() => onEdit(agent)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  <Icons.Edit /> {t.editAgent}
                </button>
                <button
                  onClick={() => onDelete(agent.id)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <Icons.Trash /> {t.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Sessions Page
function SessionsPage({ sessions, agents, t }) {
  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : 'Unknown';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[600px]">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">{t.caller}</th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Agent</th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">{t.duration}</th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">{t.status}</th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Datum</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sessions.map((session) => (
            <tr key={session.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <span className="font-medium text-gray-800">{session.caller_number || 'Unbekannt'}</span>
              </td>
              <td className="px-6 py-4 text-gray-600">{getAgentName(session.agent_id)}</td>
              <td className="px-6 py-4 text-gray-600">
                {session.duration ? `${Math.floor(session.duration / 60)}:${(session.duration % 60).toString().padStart(2, '0')}` : '-'}
              </td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  session.status === 'active' ? 'bg-green-100 text-green-700' : 
                  session.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {session.status}
                </span>
              </td>
              <td className="px-6 py-4 text-gray-600">{new Date(session.created_at).toLocaleString()}</td>
            </tr>
          ))}
          {sessions.length === 0 && (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-gray-500">{t.noSessions}</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// Transcripts Page
function TranscriptsPage({ sessions, agents, t }) {
  const sessionsWithTranscripts = sessions.filter(s => s.transcript && s.transcript.length > 0);
  const getAgentName = (agentId) => agents.find(a => a.id === agentId)?.name || 'Unknown';

  return (
    <div className="space-y-4">
      {sessionsWithTranscripts.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
          <div className="text-6xl mb-4">📝</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Keine Transkripte</h3>
          <p className="text-gray-500">Transkripte werden nach Gesprächen automatisch gespeichert.</p>
        </div>
      ) : (
        sessionsWithTranscripts.map((session) => (
          <div key={session.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">{session.caller_number || 'Unbekannt'}</h3>
                <p className="text-sm text-gray-500">{getAgentName(session.agent_id)} • {formatDateTime(session.created_at)}</p>
              </div>
            </div>
            <div className="p-6 space-y-3 max-h-64 overflow-y-auto">
              {session.transcript.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Agent Creation Wizard - Step by Step
function AgentModal({ agent, t, toolTemplates, onClose, onSave }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_prompt: 'Du bist ein freundlicher und hilfreicher Telefon-Assistent.',
    voice_engine: 'elevenlabs',
    elevenlabs_voice_id: '',
    language: 'de',
    is_active: true,
    twilio_config: { account_sid: '', auth_token: '', phone_number: '' },
    phone_number: '',
  });
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [audioRef] = useState({ current: null });

  // Load voices when reaching step 3
  useEffect(() => {
    if (step === 3 && voices.length === 0) {
      setVoicesLoading(true);
      api.get('/elevenlabs/voices').then(res => {
        if (res.success) setVoices(res.voices || []);
        setVoicesLoading(false);
      }).catch(() => setVoicesLoading(false));
    }
  }, [step]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  const playPreview = (voiceId, previewUrl) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playingVoice === voiceId) { setPlayingVoice(null); return; }
    const audio = new Audio(previewUrl);
    audio.onended = () => setPlayingVoice(null);
    audio.play().catch(() => setPlayingVoice(null));
    audioRef.current = audio;
    setPlayingVoice(voiceId);
  };

  const totalSteps = 4;
  const stepTitles = ['Name', 'Prompt', 'Stimme', 'Telefon'];

  const canProceed = () => {
    if (step === 1) return formData.name.trim().length > 0;
    if (step === 2) return formData.system_prompt.trim().length > 0;
    if (step === 3) return true; // Voice is optional, can be set later
    return true;
  };

  const handleFinish = async () => {
    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);
    if (success) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header with Progress */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800" data-testid="wizard-title">
              Neuen Agent einrichten
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="wizard-close">
              <Icons.X />
            </button>
          </div>
          {/* Step Indicator */}
          <div className="flex items-start gap-1.5">
            {stepTitles.map((title, i) => (
              <div key={i} className="flex-1 min-w-0">
                <div className={`h-1.5 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-indigo-600' : 'bg-gray-200'
                }`} />
                <p className={`text-[10px] sm:text-xs mt-1 truncate text-center ${i + 1 === step ? 'text-indigo-600 font-medium' : 'text-gray-400'}`}>
                  {title}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          {/* Step 1: Name & Description */}
          {step === 1 && (
            <div className="space-y-5" data-testid="wizard-step-1">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🤖</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Wie soll Ihr Agent heissen?</h3>
                <p className="text-sm text-gray-500 mt-1">Geben Sie Ihrem KI-Telefonassistenten einen Namen.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                  placeholder="z.B. Praxis Dr. Müller, Kundenservice..."
                  data-testid="wizard-name-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Beschreibung (optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="z.B. Terminbuchungen und Kundenanfragen"
                  data-testid="wizard-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sprache</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  data-testid="wizard-language-select"
                >
                  <option value="de">Deutsch</option>
                  <option value="en">Englisch</option>
                  <option value="es">Spanisch</option>
                  <option value="fr">Französisch</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: System Prompt */}
          {step === 2 && (
            <div className="space-y-5" data-testid="wizard-step-2">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📝</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Was soll Ihr Agent tun?</h3>
                <p className="text-sm text-gray-500 mt-1">Beschreiben Sie die Persönlichkeit und Aufgaben Ihres Agenten.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">System Prompt *</label>
                <textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                  placeholder="Du bist ein freundlicher Telefonassistent für..."
                  data-testid="wizard-prompt-input"
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  Tipp: Beschreiben Sie die Begrüßung, z.B. &quot;Beginne jedes Gespräch mit: Guten Tag, hier ist...&quot;
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Voice Selection */}
          {step === 3 && (
            <div className="space-y-5" data-testid="wizard-step-3">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🔊</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Welche Stimme soll Ihr Agent haben?</h3>
                <p className="text-sm text-gray-500 mt-1">Alle Stimmen sind mehrsprachig und sprechen Deutsch.</p>
              </div>
              <div>
                {voicesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-3 justify-center">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Verfügbare Stimmen werden geladen...
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="wizard-voice-list">
                    {voices.map(v => {
                      const isSelected = formData.elevenlabs_voice_id === v.voice_id;
                      const isPlaying = playingVoice === v.voice_id;
                      const genderLabel = v.gender === 'male' ? 'Männlich' : v.gender === 'female' ? 'Weiblich' : v.gender === 'neutral' ? 'Neutral' : '';
                      const useCaseLabel = {
                        conversational: 'Konversation',
                        narrative_story: 'Erzählung',
                        social_media: 'Social Media',
                        entertainment_tv: 'Entertainment',
                        characters_animation: 'Charakter',
                      }[v.use_case] || '';
                      return (
                        <div
                          key={v.voice_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => setFormData({ ...formData, elevenlabs_voice_id: v.voice_id })}
                          data-testid={`voice-option-${v.voice_id}`}
                        >
                          <input
                            type="radio"
                            name="voice"
                            value={v.voice_id}
                            checked={isSelected}
                            onChange={() => {}}
                            className="text-indigo-600 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800 text-sm">{v.name}</span>
                              {genderLabel && (
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                  v.gender === 'male' ? 'bg-blue-100 text-blue-700' : v.gender === 'female' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-600'
                                }`}>{genderLabel}</span>
                              )}
                              {useCaseLabel && (
                                <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{useCaseLabel}</span>
                              )}
                            </div>
                            {v.descriptive && (
                              <p className="text-xs text-gray-400 mt-0.5 capitalize">{v.descriptive}</p>
                            )}
                          </div>
                          {v.preview_url && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); playPreview(v.voice_id, v.preview_url); }}
                              className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                                isPlaying
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600'
                              }`}
                              data-testid={`voice-preview-${v.voice_id}`}
                              title="Stimme anhören"
                            >
                              {isPlaying ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {!formData.elevenlabs_voice_id && !voicesLoading && voices.length > 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    Sie können die Stimme auch später im Agent-Editor festlegen.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Phone Connection */}
          {step === 4 && (
            <div className="space-y-5" data-testid="wizard-step-4">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📞</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Telefonnummer verbinden</h3>
                <p className="text-sm text-gray-500 mt-1">Verbinden Sie eine Twilio-Nummer, damit Ihr Agent Anrufe entgegennehmen kann.</p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Optional:</strong> Sie können die Telefonnummer auch später unter &quot;Verbindungen&quot; einrichten.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefonnummer (Twilio)</label>
                <input
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="+49..."
                  data-testid="wizard-phone-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Account SID</label>
                  <input
                    type="text"
                    value={formData.twilio_config.account_sid}
                    onChange={(e) => setFormData({
                      ...formData,
                      twilio_config: { ...formData.twilio_config, account_sid: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="AC..."
                    data-testid="wizard-twilio-sid"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Auth Token</label>
                  <input
                    type="password"
                    value={formData.twilio_config.auth_token}
                    onChange={(e) => setFormData({
                      ...formData,
                      twilio_config: { ...formData.twilio_config, auth_token: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="••••••••"
                    data-testid="wizard-twilio-token"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Navigation */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                data-testid="wizard-back"
              >
                ← Zurück
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 mr-2">Schritt {step} von {totalSteps}</span>
            {step < totalSteps ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm font-medium"
                data-testid="wizard-next"
              >
                Weiter →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinish}
                disabled={saving || !formData.name.trim()}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium"
                data-testid="wizard-finish"
              >
                {saving ? 'Wird erstellt...' : 'Agent erstellen'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Agent Detail Page - Use Legacy implementation
function AgentDetailPage({ agentId, onBack, onSave, onDirtyChange }) {
  return <LegacyAgentDetailPage agentId={agentId} onBack={onBack} onSave={onSave} onDirtyChange={onDirtyChange} />;
}

// Main App wrapper with Router
function App() {
  return (
    <AppDialogProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AppDialogProvider>
  );
}

export default App;
